import { useAtomValue } from 'jotai';
import type { ShortLinkTargetType } from '@pairy/sheard/types';

import {
  getBulkLinkShareLink,
  getFileShareLink,
  getLinkShareLink,
  getShortShareLink,
} from '@/utils/shareLink';
import { userInfoAtom } from '@/state/user/atom';
import useShortShareToken, {
  pickShareAlias,
  type CustomizeDeniedReason,
} from '@/hooks/queries/share/useShortShareToken';

interface BuildLongShareUrlParams {
  targetType: ShortLinkTargetType;
  /** 파일은 hashId, 대량발송 링크는 slug */
  identifier: string;
  domain?: string;
}

/** 단축 토큰을 못 받았을 때 폴백으로 쓰는 기존 공유 URL */
export const buildLongShareUrl = ({
  targetType,
  identifier,
  domain,
}: BuildLongShareUrlParams) => {
  switch (targetType) {
    case 'BULK_FILE':
    case 'BULK_FILE_INDIVIDUAL':
      return getFileShareLink({ hashId: identifier, isBulk: true, domain });
    case 'CLIENT_FILE':
      return getFileShareLink({ hashId: identifier, isBulk: false, domain });
    case 'BULK_LINK':
      return getBulkLinkShareLink({ slug: identifier, domain });
    case 'CLIENT_LINK':
      return getLinkShareLink({ hashId: identifier, domain });
    default:
      return '';
  }
};

interface Params {
  targetType: ShortLinkTargetType;
  identifier: string;
  /** 이미 받아둔 토큰이 있으면(POST /link 응답 등) 발급 API를 부르지 않는다 */
  shortToken?: string;
  enabled?: boolean;
}

/**
 * 공유 링크 URL. 단축 토큰이 준비돼 있으면 `/s/{token}`, 아니면 기존 긴 URL을 준다.
 *
 * 토큰을 렌더 시점에 미리 받아두므로 복사 버튼은 클릭 핸들러에서 await 없이
 * 동기적으로 복사할 수 있다. 발급이 실패하거나 아직 도착하지 않았더라도 기존 URL이
 * 그대로 동작하므로 복사가 실패하는 경우는 없다.
 */
const useShareUrl = ({
  targetType,
  identifier,
  shortToken,
  enabled = true,
}: Params) => {
  const userInfo = useAtomValue(userInfoAtom);
  const domain = userInfo?.domain;

  const { data: shareInfo, isInitialLoading } = useShortShareToken(
    { targetType, hash: identifier },
    {
      enabled: enabled && Boolean(identifier) && !shortToken,
      select: (data) => data.data.data,
    },
  );

  /* shortToken 경로는 발급 쿼리가 꺼져 있어 customSlug를 모르지만, 갓 만든 링크에는
     커스텀 주소가 있을 수 없으므로 문제되지 않는다. */
  const alias =
    shortToken ?? (shareInfo ? pickShareAlias(shareInfo) : undefined);

  const customLink: {
    customSlug: string | null;
    canCustomize?: boolean;
    customizeDeniedReason: CustomizeDeniedReason | null;
  } = {
    customSlug: shareInfo?.customSlug ?? null,
    canCustomize: shareInfo?.canCustomize,
    customizeDeniedReason: shareInfo?.customizeDeniedReason ?? null,
  };

  /* 발급이 끝나지 않았어도 기존 URL을 돌려준다. QR처럼 확정된 URL이 필요한 곳만
     isLoading 을 보고 기다리면 된다. */
  const isLoading = isInitialLoading;

  if (!identifier) {
    return { url: '', isShort: false, isLoading: false, ...customLink };
  }

  if (alias) {
    return {
      url: getShortShareLink({ token: alias, domain }),
      isShort: true,
      isLoading: false,
      ...customLink,
    };
  }

  return {
    url: buildLongShareUrl({ targetType, identifier, domain }),
    isShort: false,
    isLoading,
    ...customLink,
  };
};

export default useShareUrl;
